import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton/ConfirmButton";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import {
  DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA
} from "@saleor/config";
import { voucherPath } from "@saleor/discounts/urls";
import useDownloadReport from "@saleor/hooks/useDownloadReport";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { getStringOrPlaceholder, maybe } from "@saleor/misc";
import useBrandSearch from "@saleor/searches/useBrandSearch";
import useMallSearch from "@saleor/searches/useMallSearch";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import Moment from "moment";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ReportsUrl } from "../../../overview/urls";
import { InvalidThreeMonth } from "../../../utils/misc";
import { reportsDownload } from "../../../utils/utils";
import VocuherListPage from "../../components/VoucherListPage";
import {
  useInitialVoucherReportsFilterDataQuery,
  useVoucherReportsListQuery
} from "../../queries";
import {
  voucherReportsListUrl,
  VoucherReportsListUrlDialog,
  VoucherReportsUrlQueryParams
} from "../../urls";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
interface VoucherListProp {
  params: VoucherReportsUrlQueryParams;
}

const VoucherList: React.FC<VoucherListProp> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const shop = useShop();

  const intl = useIntl();
  const dateNow = Moment(new Date()).format("YYYYMMDDHHmmss");
  const sheetName = "Voucher";
  const fileName = `${reportsDownload.voucher.filename}_${dateNow}.xlsx`;
  const { updateListSettings, settings } = useListSettings(
    ListViews.REPORTS_LIST
  );
  const paginationState = createPaginationState(settings.rowNumber, params);
  const currencySymbol = shop?.defaultCurrency || "PHP";
  const [loadData, setLoadData] = useState(true);
  const [downloadPagination, setDownloadPagination] = useState("");
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");

  React.useEffect(() => {
    if (InvalidThreeMonth(params.orderDateFrom, params.orderDateTo)) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to filter the report data. Please limit the date range to 3 months."
        })
      });
      navigate(voucherReportsListUrl());
    }
  }, [params.orderDateTo]);

  const previewVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );

  // Todo - <Marco>: Explore on how to fix from BE
  React.useEffect(() => {
    if (params.query !== null) {
      params.after = null;
      params.before = null;
    }
  }, [params]);

  const reportVariables = React.useMemo(
    () => ({
      after: downloadPagination,
      filter: getFilterVariables(params),
      first: 1000
    }),
    [params]
  );

  const { data: initialFilterData } = useInitialVoucherReportsFilterDataQuery({
    variables: {
      brand: params.brand,
      mall: params.mall
    }
  });

  const searchBrand = useBrandSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });
  const searchMall = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });

  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: voucherReportsListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    VoucherReportsListUrlDialog,
    VoucherReportsUrlQueryParams
  >(navigate, voucherReportsListUrl, params);

  const handleTabChange = (tab: number) => {
    navigate(
      voucherReportsListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(voucherReportsListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { data: previewData } = useVoucherReportsListQuery({
    displayLoader: true,
    skip: Object.keys(params).length === 0,
    variables: previewVariables
  });

  const {
    data: reportData,
    loading: reportLoading
  } = useVoucherReportsListQuery({
    displayLoader: true,
    skip: loadData,
    variables: reportVariables
  });

  React.useEffect(
    () =>
      navigate(
        voucherReportsListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    previewData?.voucherReport?.pageInfo,
    paginationState,
    params
  );
  const filterOpts = getFilterOpts(
    params,
    {
      initial: maybe(
        () => initialFilterData.brands.edges.map(edge => edge.node) || []
      ),
      search: searchBrand
    },
    {
      initial: maybe(
        () => initialFilterData.malls.edges.map(edge => edge.node) || []
      ),
      search: searchMall
    }
  );

  const selectReportData = reportData => {
    const selectedData = reportData.map(data => {
      delete data.__typename;
      delete data.id;
      data.payment_method_type = data.payment_method_type.toUpperCase();

      data.full_name = `${data.first_name}  ${data.last_name}`;

      delete data.first_name;
      delete data.last_name;
      delete data.voucher_id;
      return data;
    });
    return selectedData;
  };

  const [downloadData, setDownloadData] = useState([]);
  const shownVouchers = previewData?.voucherReport?.edges.map(
    edge => edge.node
  );
  const allVouchers = reportData?.voucherReport?.edges.map(edge => edge.node);
  const nextPage = reportData?.voucherReport?.pageInfo;
  const selectedReportData = selectReportData(downloadData?.flat());
  React.useEffect(() => {
    if (reportData && !reportLoading) {
      setDownloadData(current => [...current, allVouchers]);
      setDownloadPagination(nextPage?.endCursor);
      if (!nextPage?.hasNextPage && nextPage?.endCursor === null) {
        useDownloadReport(
          selectedReportData,
          reportsDownload.voucher.fields,
          sheetName,
          fileName
        );
        setDownloadStatus("success");
        setDownloadPagination("");
        setLoadData(true);
        setDownloadData([]);
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Voucher successfully downloaded."
          })
        });
        closeModal();
      }
    }
  }, [reportData]);

  const cancelDownload = () => {
    setLoadData(true);
    setDownloadData([]);
    setDownloadStatus("default");
    setDownloadPagination("");
    closeModal();
  };

  return (
    <>
      <VocuherListPage
        vouchers={shownVouchers}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        filterOpts={filterOpts}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onFilterChange={changeFilters}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={tabs.map(tab => tab.name)}
        disabled={reportLoading}
        pageInfo={pageInfo}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        settings={settings}
        onBack={() => navigate(ReportsUrl)}
        download={() => openModal("download")}
        onRowClick={id => () => window.open(voucherPath(id))}
      />
      <ActionDialog
        open={params.action === "download"}
        onClose={cancelDownload}
        confirmButtonState={downloadStatus}
        onConfirm={() => {
          setLoadData(false);
          setDownloadStatus("loading");
          setDownloadData([]);
          setDownloadPagination("");
        }}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Download Voucher",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download a voucher with {total} rows. Do you want to continue?"
            values={{
              total: <strong>{previewData?.voucherReport?.totalCount}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />

      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={getStringOrPlaceholder(tabs[currentTab - 1]?.name)}
      />
    </>
  );
};

export default VoucherList;
